'use client';

import { useState, useCallback, useRef } from 'react';

interface SwipeState {
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  isSwiping: boolean;
  direction: 'left' | 'right' | null;
}

interface UseSwipeGesturesOptions {
  threshold?: number; // Minimum swipe distance to trigger action
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  enabled?: boolean;
}

interface UseSwipeGesturesReturn {
  handlers: {
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchMove: (e: React.TouchEvent) => void;
    onTouchEnd: (e: React.TouchEvent) => void;
  };
  offset: number;
  isSwiping: boolean;
  direction: 'left' | 'right' | null;
  progress: number; // 0 to 1, how close to threshold
  reset: () => void;
}

export const useSwipeGestures = ({
  threshold = 100,
  onSwipeLeft,
  onSwipeRight,
  enabled = true,
}: UseSwipeGesturesOptions = {}): UseSwipeGesturesReturn => {
  const [offset, setOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [direction, setDirection] = useState<'left' | 'right' | null>(null);

  const stateRef = useRef<SwipeState>({
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
    isSwiping: false,
    direction: null,
  });

  const reset = useCallback(() => {
    setOffset(0);
    setIsSwiping(false);
    setDirection(null);
    stateRef.current = {
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0,
      isSwiping: false,
      direction: null,
    };
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!enabled) return;

    const touch = e.touches[0];
    stateRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      currentX: touch.clientX,
      currentY: touch.clientY,
      isSwiping: false,
      direction: null,
    };
  }, [enabled]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!enabled) return;

    const touch = e.touches[0];
    const state = stateRef.current;

    const deltaX = touch.clientX - state.startX;
    const deltaY = touch.clientY - state.startY;

    // Determine if this is a horizontal swipe (not vertical scroll)
    if (!state.isSwiping && Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
      state.isSwiping = true;
      setIsSwiping(true);
    }

    if (state.isSwiping) {
      e.preventDefault(); // Prevent scroll while swiping

      state.currentX = touch.clientX;
      state.currentY = touch.clientY;

      // Determine direction
      const newDirection = deltaX > 0 ? 'right' : 'left';
      state.direction = newDirection;
      setDirection(newDirection);

      // Apply resistance at edges
      const resistance = 0.5;
      const adjustedOffset = deltaX * resistance;
      setOffset(adjustedOffset);
    }
  }, [enabled]);

  const handleTouchEnd = useCallback(() => {
    if (!enabled) return;

    const state = stateRef.current;

    if (state.isSwiping) {
      const deltaX = state.currentX - state.startX;

      // Check if threshold was reached
      if (Math.abs(deltaX) >= threshold * 0.5) {
        // Trigger haptic feedback if available
        if (navigator.vibrate) {
          navigator.vibrate(10);
        }

        if (deltaX < 0 && onSwipeLeft) {
          onSwipeLeft();
        } else if (deltaX > 0 && onSwipeRight) {
          onSwipeRight();
        }
      }
    }

    // Reset with animation
    reset();
  }, [enabled, threshold, onSwipeLeft, onSwipeRight, reset]);

  const progress = Math.min(Math.abs(offset) / (threshold * 0.5), 1);

  return {
    handlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
    offset,
    isSwiping,
    direction,
    progress,
    reset,
  };
};

export default useSwipeGestures;
